import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { EMPTY_DISPLAY } from "../../utils/format";
import { MatchBasisMark } from "./MatchBasisMark";
import type { CorporateRegistryTag, ListRegistryTag } from "../../types";

interface Props {
  tag: ListRegistryTag | CorporateRegistryTag;
  /** 一覧の行用の小さい表示 */
  dense?: boolean;
}

const hasSources = (
  tag: ListRegistryTag | CorporateRegistryTag
): tag is CorporateRegistryTag => "sources" in tag;

/**
 * カーソルを当てたときの説明。名簿ごとに名前・カードの先頭行（番号など）・基準日を出す
 * @param props.tag 詳細のタグ
 * @returns 説明
 */
const TagTooltip: React.FC<{ tag: CorporateRegistryTag }> = ({ tag }) => (
  <Box sx={{ py: 0.5 }}>
    {tag.sources.map((source) => (
      <Box key={source.source} sx={{ "& + &": { mt: 1 } }}>
        <Typography variant="caption" component="div" fontWeight="bold">
          {source.sourceLabel}
        </Typography>
        {source.card?.rows[0]?.map((item) => (
          <Typography key={item.label} variant="caption" component="div">
            {item.label}：{item.value || EMPTY_DISPLAY}
          </Typography>
        ))}
        {source.asOf ? (
          <Typography variant="caption" component="div">
            基準日：{source.asOf}
          </Typography>
        ) : null}
      </Box>
    ))}
  </Box>
);

/**
 * 公表情報のタグのチップ。右端に紐付けの方法のマークを出す
 * @param props.tag タグ
 * @param props.dense 一覧の行用の小さい表示
 * @returns チップ
 */
export const RegistryTagChip: React.FC<Props> = ({ tag, dense = false }) => {
  const chip = (
    <Chip
      size="small"
      color="primary"
      variant="outlined"
      label={
        <Box
          component="span"
          sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          {tag.label}
          <MatchBasisMark basis={tag.matchBasis} />
        </Box>
      }
      // ツールチップをキーボードでも開けるようにする
      tabIndex={hasSources(tag) ? 0 : undefined}
      sx={{
        borderRadius: 0.5,
        bgcolor: "action.hover",
        fontWeight: 500,
        ...(dense ? { height: 22, fontSize: 12 } : {}),
      }}
    />
  );
  return hasSources(tag) ? (
    // スマホは押してすぐ開き、読めるだけの時間は閉じない
    <Tooltip
      title={<TagTooltip tag={tag} />}
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={5000}
    >
      {chip}
    </Tooltip>
  ) : (
    chip
  );
};
